import { useEffect } from 'react' 
 import { Link } from 'react-router-dom'

const sections = [
  {
    id: 'collection',
    icon: '📥',
    title: 'Data We Collect',
    color: '#4a9eff',
    content: `When you use SysNerve, we collect the following types of information:

**Account Information:** Your name, email address, and authentication data provided through Clerk (our identity provider). We do not store passwords directly.

**Bug Report Data:** The bug reports, stack traces, error messages, and descriptive text you submit for analysis. This data is processed in real time and stored for your personal history and session continuity.

**Uploaded Files & Images:** Screenshots (JPG, PNG, WebP) and log files (.log, .txt, .csv, .json) you attach to bug reports. These are temporarily processed by our AI engine and stored encrypted.

**Usage Metadata:** Information about your usage patterns such as number of analyses, timestamps, plan type, and feature interactions — used to improve platform reliability and performance.

**Technical Data:** IP address, browser type, operating system, and device identifiers for security, fraud prevention, and debugging purposes.

We do not collect payment information directly. All billing is handled by our payment processor (Stripe), which operates under its own privacy policy.`,
  },
  {
    id: 'usage',
    icon: '⚙️',
    title: 'How We Use Your Data',
    color: '#4ade80',
    content: `We use your data strictly to provide, maintain, and improve SysNerve's services:

**AI Analysis:** Bug reports and attachments are sent to our AI engine for processing. This is the core function of the platform.

**Product Improvement:** Aggregated, anonymized usage statistics help us improve analysis accuracy, identify performance bottlenecks, and prioritize new features.

**Communication:** We may send you product updates, security alerts, or service announcements via email. You can unsubscribe at any time.

**Support:** If you contact us for support, we may access relevant account and analysis data to assist you.

**Security & Fraud Prevention:** We analyze access patterns to detect and prevent unauthorized access, abuse, or fraudulent usage.

We do not sell your personal data to third parties. We do not use your bug report content for advertising. We do not use your data to train AI models without your explicit, opt-in consent.`,
  },
  {
    id: 'storage',
    icon: '🔒',
    title: 'Data Storage & Security',
    color: '#7c3aed',
    content: `SysNerve takes data security seriously and implements industry-standard protections:

**Encryption:** All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption. Uploaded files and bug reports are stored in encrypted object storage.

**Data Retention:** Bug analysis history is retained for 30 days on the Starter plan, 90 days on the Team plan, and 1 year on the Enterprise plan. You may delete your history at any time from your account dashboard.

**Access Controls:** Access to production data is restricted to authorized personnel only, with audit logging on all access events.

**Infrastructure:** Our infrastructure is hosted on AWS with multi-region redundancy. We undergo regular third-party security audits and penetration testing.

**Incident Response:** In the event of a data breach, we will notify affected users within 72 hours as required by applicable data protection regulations.

No system is 100% secure. While we work hard to protect your data, we cannot guarantee absolute security. We encourage you to use strong, unique passwords and enable two-factor authentication on your account.`,
  },
  {
    id: 'third-party',
    icon: '🔗',
    title: 'Third-Party Services',
    color: '#f59e0b',
    content: `SysNerve integrates with the following third-party services to deliver its features:

**Clerk (Authentication):** We use Clerk for user authentication and account management. Clerk collects your email address and identity data in accordance with their own Privacy Policy at clerk.com/legal/privacy.

**OpenAI / Anthropic (AI Engine):** Bug reports and attachments are processed by AI language model APIs (OpenAI and/or Anthropic) to generate analyses. These providers process data under strict data processing agreements that prohibit training on API-submitted data by default. Please review their respective policies at openai.com/privacy and anthropic.com/privacy.

**Stripe (Payments):** Billing and subscription management is handled by Stripe. We do not store credit card numbers. Stripe operates under PCI-DSS compliance.

**Vercel Analytics:** We use Vercel Analytics for anonymous, cookie-free usage statistics. No personally identifiable information is collected.

**AWS (Infrastructure):** Our servers, databases, and file storage run on Amazon Web Services infrastructure, covered by AWS's security and compliance frameworks.

We carefully vet all third-party providers and only share the minimum necessary data to operate our service.`,
  },
  {
    id: 'rights',
    icon: '✅',
    title: 'Your Rights',
    color: '#4ade80',
    content: `Depending on your location, you may have the following rights regarding your personal data:

**Access:** Request a copy of the personal data we hold about you.

**Correction:** Request corrections to any inaccurate or incomplete data.

**Deletion:** Request deletion of your account and all associated data ("right to be forgotten"). We will fulfill deletion requests within 30 days.

**Portability:** Request a machine-readable export of your data (JSON format).

**Objection:** Object to or restrict certain types of data processing, including profiling.

**Opt-out of AI training:** If we ever propose using your data for model training, you will have a clear opt-in mechanism. You may opt out of any such program at any time.

To exercise any of these rights, contact us at support@sysnerve.com. We will respond within 30 days. For EU/EEA residents, you also have the right to lodge a complaint with your local data protection authority.`,
  },
  {
    id: 'cookies',
    icon: '🍪',
    title: 'Cookie Policy',
    color: '#4a9eff',
    content: `SysNerve uses a minimal set of cookies to operate the platform:

**Essential Cookies:** Session cookies required for authentication and account functionality. These cannot be disabled as they are necessary for the platform to work.

**Authentication Tokens:** Clerk uses secure, HttpOnly cookies to maintain your signed-in session. These expire after 7 days of inactivity.

**Analytics:** We use Vercel Analytics, which is cookie-free and does not track individual users across sessions or sites.

We do not use advertising cookies, third-party tracking pixels, or behavioral profiling cookies. We do not sell cookie data to ad networks.

You may configure your browser to block or delete cookies, but doing so will affect your ability to use authenticated features of the platform.`,
  },
  {
    id: 'contact',
    icon: '📬',
    title: 'Contact Information',
    color: '#7c3aed',
    content: `If you have questions, concerns, or requests related to this Privacy Policy or your personal data, please contact us:

**Email:** support@sysnerve.com
**Support:** support@sysnerve.com
**Mailing Address:** SysNerve Inc., [Address on file]

For data protection inquiries from EU/EEA residents, we will appoint a Data Protection Officer (DPO) as required under GDPR upon reaching applicable thresholds.

We take all privacy inquiries seriously and aim to respond within 5 business days for general questions and within 30 days for formal data rights requests.

This Privacy Policy was last updated on January 1, 2026. We will notify you of material changes via email and by posting an updated version on this page with a revised "last updated" date.`,
  },
]

function Section({ section, index }) {
  return (
    <div style={{
      background: '#05080f',
      border: '1px solid #0f1e35',
      borderRadius: 16,
      marginBottom: 20,
      overflow: 'hidden',
      transition: 'border-color 0.3s',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = section.color + '33'}
      onMouseLeave={e => e.currentTarget.style.borderColor = '#0f1e35'}
    >
      <div style={{
        padding: '20px 28px',
        borderBottom: '1px solid #0f1e35',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: section.color + '12',
          border: `1px solid ${section.color}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, flexShrink: 0,
        }}>
          {section.icon}
        </div>
        <div>
          <div style={{ fontSize: 10, color: section.color, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>
            Section {String(index + 1).padStart(2, '0')}
          </div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#e2e8f0', fontFamily: "'Syne', sans-serif" }}>
            {section.title}
          </h2>
        </div>
      </div>
      <div style={{ padding: '22px 28px' }}>
        {section.content.split('\n\n').map((para, i) => {
          const parts = para.split(/\*\*(.+?)\*\*/g)
          return (
            <p key={i} style={{ fontSize: 14, color: '#64748b', lineHeight: 1.9, margin: '0 0 14px' }}>
              {parts.map((part, j) =>
                j % 2 === 1
                  ? <strong key={j} style={{ color: '#94a3b8', fontWeight: 700 }}>{part}</strong>
                  : part
              )}
            </p>
          )
        })}
      </div>
    </div>
  )
}

export default function Privacy() {
  useEffect(() => {
  window.scrollTo(0, 0)
}, [])
  return (
    <div style={{
      minHeight: '100vh',
      background: '#000',
      color: '#e2e8f0',
      fontFamily: "'Segoe UI', sans-serif",
      position: 'relative',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: #1e2d45; border-radius: 3px; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        h1, h2, h3 { font-family: 'Syne', sans-serif !important; }
        .back-link { color: #475569; text-decoration: none; font-size: 13px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: 1px solid #1e3a5f; border-radius: 8px; transition: all 0.2s; background: #05080f; }
        .back-link:hover { color: #4a9eff; border-color: rgba(74,158,255,0.4); background: rgba(74,158,255,0.05); }
        .toc-link { display: block; color: #475569; text-decoration: none; font-size: 13px; padding: 8px 14px; border-radius: 8px; transition: all 0.2s; border: 1px solid transparent; }
        .toc-link:hover { color: #4a9eff; background: rgba(74,158,255,0.05); border-color: rgba(74,158,255,0.2); }
      `}</style>

      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: 'linear-gradient(#1e3a5f05 1px,transparent 1px),linear-gradient(90deg,#1e3a5f05 1px,transparent 1px)',
        backgroundSize: '56px 56px',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 860, margin: '0 auto', padding: '60px 5% 100px' }}>

        <div style={{ marginBottom: 48, animation: 'fadeUp 0.5s ease' }}>
          <Link to="/" className="back-link">← Back to SysNerve</Link>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 56, animation: 'fadeUp 0.6s ease 0.1s both' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#050810', border: '1px solid #1e3a5f',
            borderRadius: 24, padding: '6px 16px', marginBottom: 24,
          }}>
            <span style={{ fontSize: 20, animation: 'float 3s ease-in-out infinite' }}>⚡</span>
            <span style={{
              fontSize: 18, fontWeight: 900,
              background: 'linear-gradient(135deg,#4a9eff,#7c3aed)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              fontFamily: "'Syne', sans-serif",
            }}>SysNerve</span>
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 900, margin: '0 0 14px', lineHeight: 1.1 }}>
            Privacy <span style={{ color: '#4a9eff' }}>Policy</span>
          </h1>
          <p style={{ color: '#475569', fontSize: 14, margin: 0, lineHeight: 1.7 }}>
            Last updated: January 1, 2026 · Effective: January 1, 2026
          </p>
          <div style={{
            marginTop: 20, display: 'inline-block',
            background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)',
            borderRadius: 10, padding: '10px 20px',
          }}>
            <span style={{ fontSize: 13, color: '#4ade80', fontWeight: 600 }}>
              🔒 We don't sell your data. We never have. We never will.
            </span>
          </div>
        </div>

        {/* Intro */}
        <div style={{
          background: '#05080f', border: '1px solid #1e3a5f',
          borderRadius: 14, padding: '20px 24px', marginBottom: 24,
          animation: 'fadeUp 0.65s ease 0.15s both',
        }}>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.9 }}>
            This Privacy Policy explains how <strong style={{ color: '#94a3b8' }}>SysNerve Inc.</strong> ("SysNerve", "we", "our", or "us") collects, uses, stores, and protects your personal information when you use our AI Bug Intelligence Platform at sysnerve.com. By using our service, you agree to the practices described in this policy.
          </p>
        </div>

        {/* Sections */}
        <div style={{ animation: 'fadeUp 0.7s ease 0.2s both' }}>
          {sections.map((section, i) => (
            <Section key={section.id} section={section} index={i} />
          ))}
        </div>

        {/* Footer note */}
        <div style={{
          marginTop: 40, textAlign: 'center',
          borderTop: '1px solid #0f1e35', paddingTop: 32,
          animation: 'fadeUp 0.8s ease 0.3s both',
        }}>
         <p style={{ fontSize: 13, color: '#334155', margin: '0 0 16px', lineHeight: 1.7 }}>
  Questions about this policy? Email us at{' '}
  <a href="mailto:support@sysnerve.com" style={{ color: '#4a9eff', textDecoration: 'none' }}>support@sysnerve.com</a>
</p>
<div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
  <Link to="/" style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>Home</Link>
  <span style={{ color: '#1e293b' }}>·</span>
  <Link to="/faq" style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>FAQ</Link>
  <span style={{ color: '#1e293b' }}>·</span>
  <Link to="/terms" style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>Terms of Service</Link>
</div>
        </div>
      </div>
    </div>
  )
}

